import { Stamp, Star, Heart, Coffee, Gift, PawPrint, Pizza, Scissors, Ticket, Shirt, Check } from "lucide-react";

export const STYLES = [
  ["uni", "Uni"],
  ["platin", "Platin"],
  ["holz", "Holz"],
  ["saison", "Saison"],
  ["espresso", "Espresso"],
  ["boutique", "Boutique"],
  ["kino", "Kino"],
  ["mint", "Mint"],
];

export const ICONS = [
  ["stamp", "Stempel", Stamp], ["star", "Stern", Star], ["heart", "Herz", Heart], ["coffee", "Kaffee", Coffee],
  ["gift", "Geschenk", Gift], ["paw", "Pfote", PawPrint], ["pizza", "Pizza", Pizza], ["scissors", "Schere", Scissors],
  ["ticket", "Ticket", Ticket], ["shirt", "Shirt", Shirt],
];
const ICON_MAP = Object.fromEntries(ICONS.map(([k, , I]) => [k, I]));

export const MADE_BY = "Kundenkarte · Made by DIMEDIAL.studio";

const hexA = (hex, a) => {
  const h = (hex || "").replace("#", "");
  return h.length === 6 ? `#${h}${a}` : null;
};

function styleBase(style, bg) {
  if (style === "platin") return { background: "linear-gradient(135deg,#e8e9ee 0%,#c9ccd6 25%,#f4f5f8 50%,#b9bcc8 75%,#e8e9ee 100%)", color: "#1a1c22" };
  if (style === "holz") return { background: "linear-gradient(135deg,#6b4423 0%,#8a5a2b 30%,#a56b36 50%,#7a4f2a 70%,#6b4423 100%)", color: "#fff" };
  if (style === "saison") return { background: "linear-gradient(140deg,#0b3d2e 0%,#145c43 45%,#7f1d1d 100%)", color: "#fff" };
  if (style === "espresso") return { background: "linear-gradient(135deg,#f4e8d6 0%,#e4caa3 45%,#caa877 100%)", color: "#4a3620" };
  if (style === "boutique") return { background: "linear-gradient(135deg,#f7f2ef 0%,#ebded7 50%,#dcc7c0 100%)", color: "#3a2f2b" };
  if (style === "kino") return { background: "linear-gradient(150deg,#140b10 0%,#3a0e16 40%,#6d1620 72%,#1a0b10 115%)", color: "#f6e3c0" };
  if (style === "mint") return { background: "linear-gradient(135deg,#eafaf1 0%,#cdeedd 50%,#a9dcc6 100%)", color: "#15433a" };
  return { background: bg || "#f5f3ec", color: textOn(bg || "#f5f3ec") };
}

// Textfarbe nach Kontrast (WCAG): auf dunklem/kräftigem Hintergrund weiß, auf hellem schwarz.
function textOn(bg) {
  const h = String(bg || "#f5f3ec").replace("#", "");
  if (h.length < 6) return "#1a1c22";
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return "#1a1c22";
  return (0.299 * r + 0.587 * g + 0.114 * b) < 140 ? "#ffffff" : "#1a1c22";
}

// Akzentfarbe tönt jeden Stil über einen weichen Eckschimmer – Stärke 0–100 steuert die Deckkraft.
function styleBg(style, bg, accent, strength = 30) {
  const base = styleBase(style, bg);
  const alpha = Math.round(Math.max(0, Math.min(100, strength)) / 100 * 128);
  if (alpha <= 0) return base;
  const a = hexA(accent, alpha.toString(16).padStart(2, "0"));
  if (!a) return base;
  const glow = `radial-gradient(135% 115% at 100% -12%, ${a} 0%, transparent 56%)`;
  return { ...base, background: `${glow}, ${base.background}` };
}

function isDark(style, bg) {
  if (style === "holz" || style === "saison" || style === "kino") return true;
  if (style === "platin" || style === "espresso" || style === "boutique" || style === "mint") return false;
  const h = (bg || "#f5f3ec").replace("#", "");
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b < 140;
}

/** Gebrandete Sammelpass-Karte – im Portal-Vorschau, Kunden-App und nach dem Stempeln. */
export function PassCard({ shop, stamps = 0, goal, justStamped = false, testId = "pass-card" }) {
  const brand = shop?.brand || {};
  const pass = shop?.pass || {};
  const N = goal ?? pass.goal ?? 10;
  const S = Math.min(stamps, N);
  const style = brand.style || "uni";
  const accent = brand.color || "#111111";
  const bgStyle = styleBg(style, brand.bg, accent, brand.accent_strength ?? 30);
  const dark = isDark(style, brand.bg);
  const Icon = ICON_MAP[pass.icon] || Stamp;
  const reward = pass.reward || "Deine Belohnung";
  const cols = N <= 6 ? N : N <= 12 ? Math.ceil(N / 2) : 5;
  const mutedCol = dark ? "rgba(255,255,255,.6)" : "rgba(0,0,0,.5)";
  const cellEmpty = dark ? "rgba(255,255,255,.12)" : "rgba(0,0,0,.07)";
  const cellBorder = dark ? "rgba(255,255,255,.22)" : "rgba(0,0,0,.14)";
  return (
    <div data-testid={testId} className="relative rounded-3xl overflow-hidden shadow-xl w-full select-none flex flex-col" style={{ ...bgStyle, aspectRatio: "0.62" }}>
      {brand.cover_url && (
        <div className="relative w-full shrink-0" style={{ height: "26%" }} data-testid="pass-card-cover">
          <img src={brand.cover_url} alt={shop?.name ? `${shop.name} – Titelbild` : "Titelbild"} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(0,0,0,.12), rgba(0,0,0,.42))" }} />
        </div>
      )}
      <div className="flex-1 min-h-0 p-5 flex flex-col">
        <div className="flex items-center gap-3 min-h-[52px]">
          {brand.logo_url ? (
            <img src={brand.logo_url} alt={shop?.name || "Logo"} className="h-12 max-w-[60%] object-contain" data-testid="pass-card-logo" />
          ) : (
            <h3 className="font-bold text-xl leading-tight" style={{ color: bgStyle.color }} data-testid="pass-card-name">{shop?.name || "Mein Geschäft"}</h3>
          )}
        </div>
        <p className="mt-1 text-xs font-medium" style={{ color: mutedCol }}>Sammelpass</p>

        <div className="mt-4 grid gap-2.5" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))` }} data-testid="pass-card-grid">
          {Array.from({ length: N }).map((_, i) => {
            const filled = i < S;
            const fresh = justStamped && i === S - 1;
            return (
              <div key={i} className={`aspect-square rounded-full grid place-items-center transition-all ${fresh ? "pass-pop" : ""}`}
                style={{ background: filled ? accent : cellEmpty, border: `2px solid ${filled ? accent : cellBorder}`, color: filled ? "#fff" : mutedCol }}
                data-testid={`pass-cell-${i}${filled ? "-filled" : ""}`}>
                {filled ? <Check className="w-[55%] h-[55%]" strokeWidth={3} /> : <Icon className="w-[50%] h-[50%]" />}
              </div>
            );
          })}
        </div>

        <div className="mt-auto pt-4">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-3xl font-extrabold leading-none" style={{ color: bgStyle.color }} data-testid="pass-card-progress">{S} <span className="text-lg font-semibold" style={{ color: mutedCol }}>/ {N}</span></p>
              <p className="text-xs mt-1" style={{ color: mutedCol }}>{S >= N ? "Belohnung bereit!" : `Noch ${N - S} bis zur Belohnung`}</p>
            </div>
            <div className="text-right max-w-[52%]">
              <p className="text-[10px] uppercase tracking-wider" style={{ color: mutedCol }}>Belohnung</p>
              <p className="text-sm font-bold leading-tight" style={{ color: accent === "#111111" && !dark ? "#111" : bgStyle.color }} data-testid="pass-card-reward">{reward}</p>
            </div>
          </div>
          <div className="mt-3 pt-2.5 flex items-center gap-1.5" style={{ borderTop: `1px solid ${cellBorder}` }}>
            <span className="inline-grid place-items-center w-4 h-4 rounded-full text-[9px] font-black" style={{ background: dark ? "rgba(255,255,255,.9)" : "#111", color: dark ? "#111" : "#FDD105" }}>R</span>
            <span className="text-[10px]" style={{ color: mutedCol }}>{MADE_BY}</span>
          </div>
        </div>
      </div>
      {S >= N && <div className="pass-ready-glow" />}
      <style>{`
        @keyframes passPop { 0%{transform:scale(0);} 60%{transform:scale(1.25);} 100%{transform:scale(1);} }
        .pass-pop { animation: passPop .5s cubic-bezier(.3,1.4,.5,1); }
        .pass-ready-glow { position:absolute; inset:0; pointer-events:none; box-shadow: inset 0 0 0 3px rgba(255,215,0,.9), inset 0 0 40px rgba(255,215,0,.35); border-radius:1.5rem; animation: passGlow 1.8s ease-in-out infinite; }
        @keyframes passGlow { 0%,100%{opacity:.5;} 50%{opacity:1;} }
      `}</style>
    </div>
  );
}
